/**
 * Navigator shell — shared panel state for the header icon, the floating
 * launcher and the command palette (PRD-navigator-that-does-it FR-0).
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { HarryPanel } from './HarryPanel';
import { HarryFloatingLauncher } from './HarryFloatingLauncher';
import { HarryShellContext } from './harryShellContext';
import { useHarryStore } from '../../stores/prioritization/harryStore';
import { useWesStore } from '../../stores/prioritization/wesStore';
import { useGuideAgentStore } from '../../stores/prioritization/guideAgentStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { isAiCapabilityEnabled } from '../../lib/ai/aiAssistSettings';
import type { CornerAssistant } from '../../lib/harry/harryPrefs';

export function HarryShell({
  children,
  suppressLauncher = false,
}: {
  children: ReactNode;
  /** Hide the floating launcher on full-viewport takeover surfaces whose own footer it would cover. */
  suppressLauncher?: boolean;
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState<string | null>(null);
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const fetchHarryConfig = useHarryStore((s) => s.fetchConfig);
  const fetchWesConfig = useWesStore((s) => s.fetchConfig);
  const fetchActiveGuide = useGuideAgentStore((s) => s.fetchActiveGuide);
  const voice = useGuideAgentStore((s) => s.activeGuide);
  const uiPrefs = useHarryStore((s) => s.uiPrefs);
  const setUiPrefs = useHarryStore((s) => s.setUiPrefs);

  useEffect(() => {
    if (workspaceId) {
      void fetchHarryConfig(workspaceId, userId);
      void fetchWesConfig(workspaceId);
      void fetchActiveGuide(workspaceId);
    }
  }, [workspaceId, userId, fetchHarryConfig, fetchWesConfig, fetchActiveGuide]);

  // Exclusive corner: Co-Facilitator owns the FAB only when selected and enabled;
  // otherwise the Navigator owns it (fallback if capability is off).
  const coFacilitatorOwnsCorner =
    uiPrefs.cornerAssistant === 'co_facilitator' &&
    isAiCapabilityEnabled('co_facilitator', workspaceId);

  const showFloating =
    !coFacilitatorOwnsCorner &&
    uiPrefs.showFloatingLauncher &&
    !panelOpen &&
    !suppressLauncher;

  const setCornerAssistant = (corner: CornerAssistant) => {
    setUiPrefs(userId, { cornerAssistant: corner });
  };

  const openWith = useCallback((query: string) => {
    setInitialQuery(query);
    setPanelOpen(true);
  }, []);

  const closePanel = () => {
    setPanelOpen(false);
    setInitialQuery(null);
  };

  return (
    <HarryShellContext.Provider
      value={{
        panelOpen,
        setPanelOpen,
        togglePanel: () => setPanelOpen((v) => !v),
        openWith,
        voice,
        cornerAssistant: uiPrefs.cornerAssistant,
        setCornerAssistant,
      }}
    >
      {children}
      {showFloating && (
        <HarryFloatingLauncher
          mode={voice}
          onOpen={() => setPanelOpen(true)}
        />
      )}
      {panelOpen && <HarryPanel initialQuery={initialQuery} onClose={closePanel} />}
    </HarryShellContext.Provider>
  );
}

/** @deprecated Use HarryShell */
export const ConciergeLauncher = HarryShell;
